import React from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { Link as RouterLink } from "react-router-dom";
import ClientDetailsForm from "./ClientDetailsForm";
import ClientContactForm from "./ClientContactForm";
import { clientSchema, IClient } from "../../data/Clients";
import NotesForm from "../misc/NotesForm";
import Button from "@mui/material/Button";

const defaultValues: IClient = {
  name: "",
  sectors: [],
  notes: [],
  contacts: [],
  address: null,
};

export default function ClientCreate() {
  React.useEffect(() => {
    document.title = "Clients";
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: IClient) =>
      axios.post<IClient>("/clients/", data).then((r) => r.data),
  });

  const methods = useForm<IClient>({
    defaultValues,
    disabled: isPending,
    resolver: yupResolver(clientSchema),
  });
  const { reset, handleSubmit } = methods;

  const handleCreate: SubmitHandler<IClient> = (data) => {
    mutate(data, {
      onSuccess: (responseData) => {
        reset(defaultValues);
        enqueueSnackbar(`${responseData.name} created.`, {
          action: (
            <IconButton
              component={RouterLink}
              to={`/clients/${responseData.id}/`}
            >
              <EditIcon sx={{ color: "white" }} />
            </IconButton>
          ),
          variant: "success",
        });
      },
      onError: () => enqueueSnackbar("Failed to create client.", { variant: "error" }),
    });
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(handleCreate)}
        autoComplete="off"
        sx={{
          display: "grid",
          columnGap: 3,
          ml: 3,
          mr: 3,
          gridTemplateRows: `auto 1fr`,
          gridTemplateColumns: `1fr 1fr 1fr 1fr`,
          gridTemplateAreas: `
                  "toolbar toolbar toolbar toolbar"
                  "client contacts notes _"
                  `,
        }}
      >
        <Box
          sx={{
            gridArea: "toolbar",
            borderBottom: 1,
            borderTop: 1,
            borderColor: "grey.300",
          }}
        >
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              CLIENT
            </Typography>
            <Button
              type="submit"
              startIcon={<SaveIcon />}
              loading={isPending}
              loadingPosition="start"
              variant="contained"
            >
              Save
            </Button>
          </Toolbar>
        </Box>
        <Box sx={{ gridArea: "client" }}>
          <ClientDetailsForm />
        </Box>
        <Box sx={{ gridArea: "contacts" }}>
          <ClientContactForm />
        </Box>
        <Box sx={{ gridArea: "notes" }}>
          <NotesForm disabled={isPending} />
        </Box>
      </Box>
    </FormProvider>
  );
}
